import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HABC_RESPONSE_OPTIONS,
  HABC_FULL_CATEGORY,
  SUBSCALE_LABELS,
  getQuizQuestions,
} from '../habcSubscales';

const RESPONSE_CLASSES = {
  0: 'border-emerald-600 bg-emerald-500 text-white shadow-md shadow-emerald-100',
  1: 'border-yellow-500 bg-yellow-300 text-[#3d2b00] shadow-md shadow-yellow-100',
  2: 'border-orange-600 bg-orange-500 text-white shadow-md shadow-orange-100',
  3: 'border-red-600 bg-red-500 text-white shadow-md shadow-red-100',
};

// Spoken alongside each question so the caregiver hears the answer scale too.
// Mirrors the wording used by the full HABC Monitor page.
export const HABC_RESPONSE_READOUT = [
  'You can choose, green for not at all.',
  'Yellow for several days.',
  'Orange for more than half the days.',
  'Red for nearly every day.',
].join(' ');

// In-chat card that administers the HABC-M one question at a time: either the
// whole monitor (category 'habc', 31 items across four sections) or a single
// subscale. Answer keys use the same `sec{sectionIndex}_q{itemIndex}` format
// as the HABC Monitor page so the answers are scored the same way.
// Pause between cutting off the previous question and reading the next one.
export const QUESTION_READ_DELAY_MS = 1000;

// `speakText` / `stopSpeaking` (optional) let the chat avatar read each
// question aloud and cut off the previous one when the question changes.
export default function SubscaleQuiz({ category, onClose, onComplete, speakText, stopSpeaking }) {
  const questions = useMemo(() => getQuizQuestions(category), [category]);
  const isFull = category === HABC_FULL_CATEGORY;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});

  const current = questions[currentQuestion];
  const currentText = current?.text || '';

  const readTimerRef = useRef(null);

  const readQuestion = () => {
    if (typeof speakText === 'function' && currentText) {
      speakText(`${currentText} ${HABC_RESPONSE_READOUT}`);
    }
  };

  // Manual re-read: cut off whatever is playing and start over immediately.
  const handleReadAgain = () => {
    clearTimeout(readTimerRef.current);
    if (typeof stopSpeaking === 'function') {
      stopSpeaking();
    }
    readQuestion();
  };

  // On open and on every question change: cut off any speech in progress,
  // pause briefly, then read the new question.
  useEffect(() => {
    if (typeof stopSpeaking === 'function') {
      stopSpeaking();
    }
    readTimerRef.current = setTimeout(readQuestion, QUESTION_READ_DELAY_MS);
    return () => {
      clearTimeout(readTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, currentText]);

  // Closing or finishing the quiz should not leave the question playing.
  useEffect(() => {
    return () => {
      if (typeof stopSpeaking === 'function') {
        stopSpeaking();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!current) {
    return null;
  }

  const total = questions.length;
  const answerKey = current.key;
  const selectedValue = answers[answerKey];
  const isLast = currentQuestion === total - 1;
  const label = SUBSCALE_LABELS[category] || current.sectionTitle;
  const progressPercent = Math.round(((currentQuestion + 1) / total) * 100);

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [answerKey]: value }));
  };

  const handleBack = () => {
    setCurrentQuestion((q) => Math.max(0, q - 1));
  };

  const handleNext = () => {
    if (selectedValue == null) {
      return;
    }

    if (isLast) {
      onComplete(answers);
      return;
    }

    setCurrentQuestion((q) => q + 1);
  };

  // Rendered in place of the chat panel (no backdrop) so the avatar beside it
  // stays visible while reading the questions aloud.
  return (
    <div
      className="vica-subscale-card relative flex w-full flex-col overflow-y-auto vica-scrollbar rounded-[18px] bg-white shadow-[0_24px_70px_rgba(0,52,80,0.28)] border border-[#cfe2ee]"
      role="region"
      aria-labelledby="subscale-quiz-title"
    >
        <button
          type="button"
          className="absolute right-4 top-4 h-12 w-12 rounded-full border border-white/40 bg-white/15 text-white text-2xl leading-none font-bold"
          onClick={onClose}
          aria-label="Close questionnaire"
          title="Close"
        >
          x
        </button>

        <div className="bg-gradient-to-br from-[#004f79] via-[#006298] to-[#2a8bc7] px-8 py-7 pr-16 text-white">
          <div className="text-[1.1rem] font-extrabold tracking-[0.14em] uppercase opacity-90">
            HABC Monitor
          </div>
          <h2 id="subscale-quiz-title" className="m-0 mt-2 text-[2.2rem] font-extrabold">
            {label} Questions
          </h2>
          <p className="m-0 mt-2 text-[1.25rem] leading-8 text-[#e8f6ff]">
            Please answer based on the past two weeks, going with your first reaction.
          </p>
        </div>

        <div className="flex flex-1 flex-col px-8 py-7 text-[#0f2b3a]">
          <div className="mb-2 flex items-center justify-between text-[1.3rem] font-bold text-[#3f6173]">
            <span>
              {isFull
                ? `${current.sectionTitle} (section ${current.sectionPosition} of ${current.sectionCount}) · Question ${currentQuestion + 1} of ${total}`
                : `Question ${currentQuestion + 1} of ${total}`}
            </span>
            <span>{progressPercent}%</span>
          </div>
          <div className="mb-6 h-4 w-full overflow-hidden rounded-full bg-[#e3eef5]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#006298] to-[#2a8bc7] transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#dceaf2] bg-[#f5fbff] px-6 py-5 text-[1.6rem] font-bold leading-[2.3rem] text-[#12384b]">
            <span className="flex-1">{currentText}</span>
            {typeof speakText === 'function' && (
              <button
                type="button"
                className="vica-btn-secondary vica-session-btn-new shrink-0"
                onClick={handleReadAgain}
                aria-label="Read this question aloud again"
                title="Read again"
              >
                🔊 Read
              </button>
            )}
          </div>

          <div className="space-y-3.5">
            {HABC_RESPONSE_OPTIONS.map((option) => {
              const isSelected = selectedValue === option.value;
              const selectedRing = isSelected
                ? 'ring-4 ring-[#006298]/30 scale-[1.01]'
                : 'opacity-90 hover:opacity-100';

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full rounded-xl border px-6 py-5 text-left text-[1.4rem] font-extrabold transition ${RESPONSE_CLASSES[option.value]} ${selectedRing}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="mt-auto flex items-center justify-between pt-7">
            <button
              type="button"
              className="vica-btn-secondary vica-tour-btn"
              onClick={handleBack}
              disabled={currentQuestion === 0}
            >
              Back
            </button>
            <button
              type="button"
              className="vica-btn vica-tour-btn-primary"
              onClick={handleNext}
              disabled={selectedValue == null}
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
    </div>
  );
}
